// MARK://dhk8/pos/lib/seed-preview
// Deterministic seed data for the no-backend preview build (`VITE_API_STUB=1`,
// e.g. compose.preview.yml). The floor / menu / orders clients return this instead
// of hitting a backend, so /floor, /menu, /order and /pos/barra render with content.
// NOT used in real builds (the flag is off) — there it'll be replaced by the
// TrainingDataProvider once that lands.

import type { FloorState } from './clients/floor.js'
import type { FlatMenu } from './clients/catalog.js'
import type { BackendOrderDto, BackendOrderItemDto, BackendOrderSubAccountDto } from './clients/orders.js'
import type { ReceiptDto } from './clients/receipts.js'
import type { OrderDto, OrderItem, RegisteredPayment } from '../stores/order-store.js'

export const PREVIEW_STUB = (import.meta.env['VITE_API_STUB'] as string | undefined) === '1'

export const SEED_FLOOR_STATE: FloorState = {
  plan: { id: 'plan-demo', widthPx: 920, heightPx: 640 },
  mapId: 'plan-demo',
  sections: [
    { id: 'sec-sala', name: 'Salón', color: null, tableCodes: ['M1', 'M2', 'M3', 'M4'] },
    { id: 'sec-terraza', name: 'Terraza', color: null, tableCodes: [] },
  ],
  activeSessions: [
    { id: 's1', tableId: 't3', covers: 2, openedAt: new Date(Date.now() - 28 * 60_000).toISOString(), userId: 'u-demo' },
    { id: 's2', tableId: 't7', covers: 4, openedAt: new Date(Date.now() - 12 * 60_000).toISOString(), userId: 'u-demo' },
  ],
  tables: [
    { id: 't1', code: 'M1', capacity: 2, status: 'AVAILABLE', posX: 60, posY: 60, widthPx: 110, heightPx: 110, shape: 'ROUND', sectionId: 'sec-sala' },
    { id: 't2', code: 'M2', capacity: 2, status: 'AVAILABLE', posX: 220, posY: 60, widthPx: 110, heightPx: 110, shape: 'ROUND', sectionId: 'sec-sala' },
    { id: 't3', code: 'M3', capacity: 4, status: 'OCCUPIED', posX: 380, posY: 60, widthPx: 150, heightPx: 110, shape: 'RECT', sectionId: 'sec-sala' },
    { id: 't4', code: 'M4', capacity: 4, status: 'RESERVED', posX: 580, posY: 60, widthPx: 150, heightPx: 110, shape: 'RECT', sectionId: 'sec-sala' },
    { id: 't5', code: 'M5', capacity: 6, status: 'AVAILABLE', posX: 60, posY: 230, widthPx: 200, heightPx: 120, shape: 'RECT', sectionId: 'sec-sala' },
    { id: 't6', code: 'B1', capacity: 1, status: 'AVAILABLE', posX: 320, posY: 240, widthPx: 90, heightPx: 90, shape: 'ROUND', sectionId: 'sec-barra' },
    { id: 't7', code: 'B2', capacity: 2, status: 'OCCUPIED', posX: 440, posY: 240, widthPx: 90, heightPx: 90, shape: 'ROUND', sectionId: 'sec-barra' },
    { id: 't8', code: 'B3', capacity: 2, status: 'CLEANING', posX: 560, posY: 240, widthPx: 90, heightPx: 90, shape: 'ROUND', sectionId: 'sec-barra' },
    { id: 't9', code: 'T1', capacity: 4, status: 'AVAILABLE', posX: 60, posY: 420, widthPx: 150, heightPx: 110, shape: 'RECT', sectionId: 'sec-terraza' },
    { id: 't10', code: 'T2', capacity: 4, status: 'AVAILABLE', posX: 260, posY: 420, widthPx: 150, heightPx: 110, shape: 'RECT', sectionId: 'sec-terraza' },
  ],
}

type SeedStock = 'OK' | 'LOW' | 'OUT' | 'UNTRACKED'
const cat = (id: string, name: string, sortOrder: number): FlatMenu['categories'][number] => ({ id, name, sortOrder })
const prod = (id: string, name: string, cents: number, categoryId: string, stockStatus: SeedStock = 'OK'): FlatMenu['products'][number] => ({
  id, name, sku: id.toUpperCase(), unitPriceCents: cents, taxRate: '0.16', isAvailable: stockStatus !== 'OUT', categoryId, stockStatus,
})

export const SEED_MENU: FlatMenu = {
  categories: [cat('c-entradas', 'Entradas', 1), cat('c-fuertes', 'Platos fuertes', 2), cat('c-bebidas', 'Bebidas', 3), cat('c-postres', 'Postres', 4)],
  products: [
    prod('p-guac', 'Guacamole con totopos', 13500, 'c-entradas'),
    prod('p-queso', 'Queso fundido con chorizo', 16500, 'c-entradas'),
    prod('p-ceviche', 'Ceviche de pescado', 18900, 'c-entradas', 'LOW'),
    prod('p-tacos', 'Orden de tacos al pastor (4)', 14900, 'c-fuertes'),
    prod('p-arrachera', 'Arrachera con guarnición', 32900, 'c-fuertes'),
    prod('p-pescado', 'Pescado a la talla', 29500, 'c-fuertes'),
    prod('p-enchiladas', 'Enchiladas verdes', 16900, 'c-fuertes'),
    prod('p-pozole', 'Pozole rojo', 13900, 'c-fuertes', 'OUT'),
    prod('p-agua', 'Agua fresca del día', 4500, 'c-bebidas'),
    prod('p-refresco', 'Refresco', 3900, 'c-bebidas'),
    prod('p-cerveza', 'Cerveza nacional', 5900, 'c-bebidas'),
    prod('p-mezcal', 'Mezcal artesanal (caballito)', 12900, 'c-bebidas'),
    prod('p-flan', 'Flan napolitano', 7900, 'c-postres'),
    prod('p-pastel', 'Pastel de tres leches', 8900, 'c-postres'),
  ],
}

// ── "Variable / customizable" product metadata (preview only) ────────────────────
// Drives the AddItemDialog "personalizar" flow: variant options (single-select, price
// deltas relative to the product base) + modifier groups (min/max, required). In a
// real build this would come from dhk8-catalog-products / dhk8-catalog-modifiers.

export interface PreviewVariantOption { id: string; name: string; priceDeltaCents: number }
export interface PreviewVariantSet { name: string; options: PreviewVariantOption[] }
export interface PreviewModifierOption { id: string; name: string; priceDeltaCents: number }
export interface PreviewModifierGroup {
  id: string
  name: string
  minSelect: number
  maxSelect: number
  isRequired: boolean
  options: PreviewModifierOption[]
}
export interface PreviewProductExtras {
  variants?: PreviewVariantSet
  modifierGroups?: PreviewModifierGroup[]
}

export const SEED_PRODUCT_EXTRAS: Record<string, PreviewProductExtras> = {
  // Arrachera — required "Término" single-select, plus optional "Acompañamientos".
  'p-arrachera': {
    modifierGroups: [
      {
        id: 'mg-termino', name: 'Término', minSelect: 1, maxSelect: 1, isRequired: true,
        options: [
          { id: 'mo-rojo', name: 'Rojo', priceDeltaCents: 0 },
          { id: 'mo-medio', name: 'Medio', priceDeltaCents: 0 },
          { id: 'mo-tres-cuartos', name: 'Tres cuartos', priceDeltaCents: 0 },
          { id: 'mo-bien-cocido', name: 'Bien cocido', priceDeltaCents: 0 },
        ],
      },
      {
        id: 'mg-extras-arr', name: 'Extras', minSelect: 0, maxSelect: 3, isRequired: false,
        options: [
          { id: 'mo-queso', name: 'Queso fundido', priceDeltaCents: 3500 },
          { id: 'mo-guacamole', name: 'Guacamole', priceDeltaCents: 4500 },
          { id: 'mo-frijoles', name: 'Frijoles charros', priceDeltaCents: 2500 },
        ],
      },
    ],
  },
  // Tacos — pick a "Tortilla" variant (price delta) + optional "Extras".
  'p-tacos': {
    variants: {
      name: 'Tortilla',
      options: [
        { id: 'va-maiz', name: 'Maíz', priceDeltaCents: 0 },
        { id: 'va-harina', name: 'Harina', priceDeltaCents: 0 },
        { id: 'va-nopal', name: 'Nopal', priceDeltaCents: 1500 },
      ],
    },
    modifierGroups: [
      {
        id: 'mg-extras-tacos', name: 'Extras', minSelect: 0, maxSelect: 4, isRequired: false,
        options: [
          { id: 'mo-queso-t', name: 'Extra queso', priceDeltaCents: 1000 },
          { id: 'mo-guac-t', name: 'Guacamole', priceDeltaCents: 1500 },
          { id: 'mo-pina', name: 'Piña', priceDeltaCents: 0 },
          { id: 'mo-cebolla', name: 'Cebolla y cilantro', priceDeltaCents: 0 },
        ],
      },
      {
        id: 'mg-salsa', name: 'Salsa', minSelect: 0, maxSelect: 2, isRequired: false,
        options: [
          { id: 'mo-verde', name: 'Verde', priceDeltaCents: 0 },
          { id: 'mo-roja', name: 'Roja', priceDeltaCents: 0 },
          { id: 'mo-habanero', name: 'Habanero', priceDeltaCents: 0 },
        ],
      },
    ],
  },
  // Agua fresca — "Tamaño" variant with price deltas.
  'p-agua': {
    variants: {
      name: 'Tamaño',
      options: [
        { id: 'va-chico', name: 'Chico', priceDeltaCents: -1000 },
        { id: 'va-mediano', name: 'Mediano', priceDeltaCents: 0 },
        { id: 'va-grande', name: 'Grande', priceDeltaCents: 1500 },
        { id: 'va-jarra', name: 'Jarra (1 L)', priceDeltaCents: 5500 },
      ],
    },
  },
}

/** Look up the preview "personalizar" metadata for a product (empty when none). */
export function previewProductExtras(productId: string): PreviewProductExtras {
  return PREVIEW_STUB ? (SEED_PRODUCT_EXTRAS[productId] ?? {}) : {}
}

// ┌─────────────────────────────────────────────────────────────────────────────┐
// │ REGION: product display metadata  (owner: pos-ui / Menú detail dialog)        │
// │ Purely additive. Decorates SEED_MENU products with store-style fields the      │
// │ rich /menu detail dialog shows (photo, description, trend, "más vendido").     │
// │ Real builds don't use this — it's preview-only flavour text. Keep merges       │
// │ trivial by leaving this block self-contained.                                  │
// └─────────────────────────────────────────────────────────────────────────────┘

/** Popularity hint shown as a small "Tendencia" chip. */
export type ProductTrend = 'up' | 'flat' | 'down'

/** Optional store-style display fields layered onto a catalog product for the detail dialog. */
export interface ProductDisplayMeta {
  /** Hero photo URL. Undefined ⇒ the dialog renders a tasteful tinted placeholder. */
  imageUrl?: string
  description?: string
  trend?: ProductTrend
  /** "★ Más vendido" badge. */
  bestSeller?: boolean
  /** For tracked items: an approximate on-hand portion count to show under the badge. */
  unitsOnHand?: number
}

// A handful of stable placeholder photos (picsum is keyed by seed so the URL is
// deterministic). If a photo fails to load (offline demo) the dialog falls back to
// the tinted placeholder, so this stays graceful either way.
const pic = (seed: string) => `https://picsum.photos/seed/${seed}/640/420`

/** Display metadata keyed by SEED_MENU productId. Anything not listed renders plain. */
export const SEED_PRODUCT_DISPLAY: Record<string, ProductDisplayMeta> = {
  // best-sellers, varied trend
  'p-tacos': {
    imageUrl: pic('dhk8-tacos'),
    description: 'Cuatro tacos al pastor con piña, cebolla y cilantro. Tortilla a tu elección y la salsa que más te lata.',
    trend: 'up',
    bestSeller: true,
    unitsOnHand: 38,
  },
  'p-arrachera': {
    imageUrl: pic('dhk8-arrachera'),
    description: 'Arrachera de res al término que prefieras, con guarnición de la casa. Agrega frijoles charros o queso fundido.',
    trend: 'flat',
    bestSeller: true,
    unitsOnHand: 14,
  },
  'p-guac': {
    imageUrl: pic('dhk8-guac'),
    description: 'Guacamole hecho al momento con aguacate Hass, jitomate, cebolla morada y cilantro. Servido con totopos crujientes.',
    trend: 'up',
    bestSeller: true,
  },
  // trending down + low stock
  'p-ceviche': {
    imageUrl: pic('dhk8-ceviche'),
    description: 'Pescado fresco curado en limón con pepino, cebolla y chile serrano. Acompañado de tostadas.',
    trend: 'down',
    unitsOnHand: 3,
  },
  // agotado
  'p-pozole': {
    imageUrl: pic('dhk8-pozole'),
    description: 'Pozole rojo de maíz cacahuazintle con carne de cerdo. Se sirve con lechuga, rábano, orégano y limón.',
    trend: 'down',
    unitsOnHand: 0,
  },
  // plain-ish entries (placeholder photo, light copy)
  'p-queso': {
    description: 'Queso Oaxaca fundido con chorizo. Para acompañar con tortillas de maíz recién hechas.',
    trend: 'flat',
  },
  'p-enchiladas': {
    imageUrl: pic('dhk8-enchiladas'),
    description: 'Tortillas de maíz rellenas de pollo, bañadas en salsa verde, con crema, queso fresco y cebolla.',
    trend: 'flat',
  },
  'p-agua': {
    description: 'Agua fresca del día preparada en casa. Pregunta el sabor — o elige el tamaño que más te refresque.',
    trend: 'up',
  },
  'p-flan': {
    imageUrl: pic('dhk8-flan'),
    description: 'Flan napolitano sedoso con caramelo. La forma correcta de cerrar la comida.',
    trend: 'flat',
    bestSeller: true,
  },
  'p-mezcal': {
    description: 'Mezcal artesanal de agave espadín, servido en caballito con naranja y sal de gusano.',
    trend: 'up',
  },
}

/** Look up the display metadata for a product (empty object when none). */
export function productDisplayMeta(productId: string): ProductDisplayMeta {
  return SEED_PRODUCT_DISPLAY[productId] ?? {}
}

// ── End REGION: product display metadata ────────────────────────────────────────

// ── Seed orders (for /order in the no-backend preview) ──────────────────────────
// Two open orders: one DINE_IN already split into 2 sub-accounts (exercises the split
// flow + sub-account tabs), one TAKEOUT flat. Item productIds match SEED_MENU so the
// MenuGrid + product picker stay consistent. Totals are tax-exclusive and recomputed
// by recomputeStubOrder() — mutations (lib/clients/orders.ts stub branch) rebuild them.

const ISO = (minutesAgo: number) => new Date(Date.now() - minutesAgo * 60_000).toISOString()
const SEED_TENANT = 'tn-demo'
const SEED_BRANCH = 'br-demo'
const SEED_USER = 'u-demo'

function priceOf(productId: string): { name: string; unitPriceCents: number; taxRate: string } {
  const p = SEED_MENU.products.find((x) => x.id === productId)
  return p ? { name: p.name, unitPriceCents: p.unitPriceCents, taxRate: p.taxRate } : { name: productId, unitPriceCents: 0, taxRate: '0.16' }
}

interface SeedItemSpec {
  id: string
  productId: string
  quantity: number
  subAccountId?: string | null
  status?: BackendOrderItemDto['status']
  modifiers?: { id: string; modifierId: string; name: string; priceDeltaCents: number }[]
  notes?: string | null
}

function makeItem(orderId: string, spec: SeedItemSpec): BackendOrderItemDto {
  const { name, unitPriceCents, taxRate } = priceOf(spec.productId)
  const modifiers = (spec.modifiers ?? []).map((m) => ({ orderItemId: spec.id, ...m }))
  const modDelta = modifiers.reduce((s, m) => s + m.priceDeltaCents, 0)
  const lineBase = (unitPriceCents + modDelta) * spec.quantity
  const rate = Number(taxRate) || 0
  const taxCents = Math.round(lineBase * rate)
  return {
    id: spec.id,
    orderId,
    subAccountId: spec.subAccountId ?? null,
    productId: spec.productId,
    variantId: null,
    variantName: null,
    comboId: null,
    name,
    quantity: spec.quantity,
    unitPriceCents,
    discountCents: 0,
    taxRate,
    taxCents,
    totalCents: lineBase,
    status: spec.status ?? 'PENDING',
    kitchenStationId: null,
    sentToKitchenAt: spec.status && spec.status !== 'PENDING' ? ISO(8) : null,
    readyAt: null,
    deliveredAt: null,
    notes: spec.notes ?? null,
    parentOrderItemId: null,
    isComboRoot: false,
    createdAt: ISO(20),
    modifiers,
  }
}

function makeSubAccount(orderId: string, id: string, label: string): BackendOrderSubAccountDto {
  return { id, orderId, label, discountCents: 0, tipCents: 0, notes: null, createdAt: ISO(15), closedAt: null }
}

/** Recompute the tax-exclusive totals of a (stub) order from its live items. Mutations
 *  call this so the UI sees consistent numbers without a server round-trip. */
export function recomputeStubOrder(o: BackendOrderDto): BackendOrderDto {
  const live = o.items.filter((i) => i.status !== 'CANCELLED')
  const subtotalCents = live.reduce((s, i) => s + i.totalCents, 0)
  const taxCents = live.reduce((s, i) => s + i.taxCents, 0)
  const discountCents = o.discountCents
  const subDiscountCents = o.subAccounts.reduce((s, sa) => s + sa.discountCents, 0)
  const tipCents = o.tipCents
  const totalCents = Math.max(0, subtotalCents - discountCents - subDiscountCents) + taxCents + tipCents
  let status = o.status
  if (status === 'OPEN' && live.some((i) => i.status !== 'PENDING')) status = 'SENT_TO_KITCHEN'
  if (o.paidCents > 0 && o.paidCents < totalCents) status = 'PARTIALLY_PAID'
  if (totalCents > 0 && o.paidCents >= totalCents) status = 'CLOSED'
  return { ...o, subtotalCents, taxCents, discountCents, tipCents, totalCents, status }
}

function buildOrder(args: {
  id: string
  folio: string
  type: BackendOrderDto['type']
  covers: number
  tableSessionId: string | null
  subAccounts: { id: string; label: string }[]
  items: SeedItemSpec[]
  openedMinutesAgo: number
}): BackendOrderDto {
  const subAccounts = args.subAccounts.map((s) => makeSubAccount(args.id, s.id, s.label))
  const items = args.items.map((spec) => makeItem(args.id, spec))
  const base: BackendOrderDto = {
    id: args.id,
    tenantId: SEED_TENANT,
    branchId: SEED_BRANCH,
    folio: args.folio,
    tableSessionId: args.tableSessionId,
    externalOrderId: null,
    customerId: null,
    covers: args.covers,
    type: args.type,
    status: 'OPEN',
    openedByUserId: SEED_USER,
    closedByUserId: null,
    subtotalCents: 0,
    discountCents: 0,
    discountMeta: null,
    taxCents: 0,
    tipCents: 0,
    totalCents: 0,
    paidCents: 0,
    notes: null,
    openedAt: ISO(args.openedMinutesAgo),
    closedAt: null,
    cancelledAt: null,
    cancelReason: null,
    globalInvoiceId: null,
    items,
    subAccounts,
  }
  return recomputeStubOrder(base)
}

export const SEED_ORDERS: BackendOrderDto[] = [
  buildOrder({
    id: 'ord-demo-1',
    folio: 'A-1042',
    type: 'DINE_IN',
    covers: 4,
    tableSessionId: 's1',
    openedMinutesAgo: 22,
    subAccounts: [
      { id: 'sa-1', label: 'Cuenta A' },
      { id: 'sa-2', label: 'Cuenta B' },
    ],
    items: [
      { id: 'oi-1', productId: 'p-guac', quantity: 1, subAccountId: 'sa-1', status: 'SENT', modifiers: [{ id: 'm-1', modifierId: 'mod-extra-totopos', name: 'Extra totopos', priceDeltaCents: 2500 }] },
      { id: 'oi-2', productId: 'p-arrachera', quantity: 1, subAccountId: 'sa-1', status: 'SENT', notes: 'Término medio' },
      { id: 'oi-3', productId: 'p-mezcal', quantity: 2, subAccountId: 'sa-1', status: 'DELIVERED' },
      { id: 'oi-4', productId: 'p-tacos', quantity: 2, subAccountId: 'sa-2', status: 'SENT' },
      { id: 'oi-5', productId: 'p-agua', quantity: 2, subAccountId: 'sa-2', status: 'DELIVERED' },
      { id: 'oi-6', productId: 'p-flan', quantity: 1, subAccountId: null, status: 'PENDING' },
    ],
  }),
  buildOrder({
    id: 'ord-demo-2',
    folio: 'B-2087',
    type: 'TAKEOUT',
    covers: 1,
    tableSessionId: null,
    openedMinutesAgo: 6,
    subAccounts: [],
    items: [
      { id: 'oi-7', productId: 'p-pescado', quantity: 1, status: 'PENDING' },
      { id: 'oi-8', productId: 'p-enchiladas', quantity: 1, status: 'PENDING', modifiers: [{ id: 'm-2', modifierId: 'mod-sin-cebolla', name: 'Sin cebolla', priceDeltaCents: 0 }] },
      { id: 'oi-9', productId: 'p-refresco', quantity: 2, status: 'PENDING' },
    ],
  }),
]

/** The order /order rehydrates by default in the preview (getOrder fallback). */
export const SEED_ORDER: BackendOrderDto = SEED_ORDERS[0] as BackendOrderDto

/** Mint a fresh empty order (for the stub `createOrder` — opening a new mesa). */
export function makeStubOrder(input: { type?: BackendOrderDto['type']; covers?: number; tableSessionId?: string | null }): BackendOrderDto {
  const n = Math.floor(Math.random() * 9000) + 1000
  return buildOrder({
    id: `ord-stub-${Date.now().toString(36)}`,
    folio: `C-${n}`,
    type: input.type ?? 'DINE_IN',
    covers: input.covers ?? 1,
    tableSessionId: input.tableSessionId ?? null,
    openedMinutesAgo: 0,
    subAccounts: [],
    items: [],
  })
}

// ── Seed receipts (for /receipt in the no-backend preview) ──────────────────────
// Two CLOSED orders with their full ticket data (items → totals, payments, change,
// tip). One is invoiced — carries a CFDI 4.0 block (UUID, fecha de timbrado, estado
// SAT = TIMBRADA, RFC + razón social del receptor, links XML/PDF placeholder). The
// other has no factura, so the /receipt page shows the "Facturar" placeholder CTA.
// Item productIds match SEED_MENU so prices stay consistent with /menu and /order.

interface ReceiptItemSpec { id: string; productId: string; quantity: number; notes?: string | null }
interface ReceiptPaySpec {
  id: string
  method: RegisteredPayment['method']
  amountCents: number
  tipCents?: number
  cardLastFour?: string | null
  referenceCode?: string | null
}

function makeReceiptItem(spec: ReceiptItemSpec): OrderItem {
  const { name, unitPriceCents, taxRate } = priceOf(spec.productId)
  return {
    id: spec.id,
    productId: spec.productId,
    name,
    quantity: spec.quantity,
    unitPriceCents,
    totalCents: unitPriceCents * spec.quantity,
    taxRate,
    modifiers: [],
    notes: spec.notes ?? undefined,
    status: 'DELIVERED',
  }
}

function buildReceipt(args: {
  id: string
  folio: string
  type: OrderDto['type']
  covers: number
  tableCode: string | null
  waiterName: string | null
  closedMinutesAgo: number
  items: ReceiptItemSpec[]
  tipCents: number
  /** Built from the computed grand total (subtotal + IVA + tip) so the numbers always cuadran. */
  payments: (totalCents: number) => ReceiptPaySpec[]
  /** Cash handed by the customer — when set, `payments` should cover the total in CASH. */
  cashGivenCents?: number | null
  cfdi?: (totalCents: number) => ReceiptDto['cfdi']
}): ReceiptDto {
  const items = args.items.map(makeReceiptItem)
  const subtotalCents = items.reduce((s, i) => s + i.totalCents, 0)
  const taxCents = items.reduce((s, i) => s + Math.round(i.totalCents * (Number(i.taxRate) || 0)), 0)
  const totalCents = subtotalCents + taxCents + args.tipCents
  const closedAt = ISO(args.closedMinutesAgo)
  const order: OrderDto = {
    id: args.id,
    folio: args.folio,
    type: args.type,
    status: 'CLOSED',
    covers: args.covers,
    items,
    subAccounts: [],
    subtotalCents,
    taxCents,
    discountCents: 0,
    totalCents,
    tipCents: args.tipCents,
    paidCents: totalCents,
    openedAt: ISO(args.closedMinutesAgo + 35),
    closedAt,
  }
  const payments: RegisteredPayment[] = args.payments(totalCents).map((p) => ({
    id: p.id,
    method: p.method,
    amountCents: p.amountCents,
    tipCents: p.tipCents ?? 0,
    cardLastFour: p.cardLastFour ?? null,
    referenceCode: p.referenceCode ?? null,
    createdAt: closedAt,
    subAccountId: null,
  }))
  const paidSum = payments.reduce((s, p) => s + p.amountCents, 0)
  const cashPaid = payments.filter((p) => p.method === 'CASH').reduce((s, p) => s + p.amountCents, 0)
  const changeCents = args.cashGivenCents ? Math.max(0, args.cashGivenCents - cashPaid) : Math.max(0, paidSum - totalCents)
  return {
    id: args.id,
    order,
    payments,
    tipCents: args.tipCents,
    cashGivenCents: args.cashGivenCents ?? null,
    changeCents,
    waiterName: args.waiterName,
    tableCode: args.tableCode,
    closedAt,
    cfdi: args.cfdi ? args.cfdi(totalCents) : null,
  }
}

export const SEED_RECEIPTS: ReceiptDto[] = [
  buildReceipt({
    id: 'rcpt-demo-1',
    folio: 'A-1039',
    type: 'DINE_IN',
    covers: 2,
    tableCode: 'M2',
    waiterName: 'Marisol R.',
    closedMinutesAgo: 14,
    items: [
      { id: 'ri-1', productId: 'p-guac', quantity: 1 },
      { id: 'ri-2', productId: 'p-arrachera', quantity: 1, notes: 'Término medio' },
      { id: 'ri-3', productId: 'p-pescado', quantity: 1 },
      { id: 'ri-4', productId: 'p-agua', quantity: 2 },
      { id: 'ri-5', productId: 'p-flan', quantity: 1 },
    ],
    tipCents: 15000,
    payments: (total) => [{ id: 'rp-1', method: 'CARD_CREDIT', amountCents: total, cardLastFour: '4242', referenceCode: 'AUTH 084213' }],
    cfdi: (total) => ({
      uuid: 'A1B2C3D4-5E6F-4A7B-8C9D-0E1F2A3B4C5D',
      serie: 'A',
      folio: '1039',
      stampedAt: ISO(12),
      satStatus: 'TIMBRADA',
      rfcReceptor: 'DUGG960512QX3',
      razonSocialReceptor: 'GUSTAVO DURAN GARCIA',
      usoCfdi: 'G03 — Gastos en general',
      totalCents: total,
      xmlUrl: '#',
      pdfUrl: '#',
    }),
  }),
  buildReceipt({
    id: 'rcpt-demo-2',
    folio: 'B-2071',
    type: 'TAKEOUT',
    covers: 1,
    tableCode: null,
    waiterName: 'Caja 1',
    closedMinutesAgo: 6,
    items: [
      { id: 'ri-6', productId: 'p-tacos', quantity: 2 },
      { id: 'ri-7', productId: 'p-cerveza', quantity: 2 },
      { id: 'ri-8', productId: 'p-pastel', quantity: 1 },
    ],
    tipCents: 0,
    payments: (total) => [{ id: 'rp-2', method: 'CASH', amountCents: total }],
    cashGivenCents: 70_000,
  }),
]

/** The receipt /receipt rehydrates by default in the preview (getReceipt fallback). */
export const SEED_RECEIPT: ReceiptDto = SEED_RECEIPTS[0] as ReceiptDto

// ════════════════════════════════════════════════════════════════════════════════
// REGION: cash-drawer / shift seed (for /caja in the no-backend preview)  [P5]
// Purely additive — owned by the /caja agent. The cashcuts client imports SEED_SHIFT
// lazily and clones it into its in-memory `stubShift`. Denomination values are in
// cents (50000 = $500 bill), matching MXN_DENOMINATIONS in lib/money.ts.
// ════════════════════════════════════════════════════════════════════════════════

import type { OpenShift } from './clients/cashcuts.js'

const SHIFT_OPENED_MS_AGO = 5 * 60 * 60 * 1000 // opened ~5h ago

/** A demo open shift: $1,500 opening float + a couple of cash movements. */
export const SEED_SHIFT: OpenShift = {
  id: 'shift-demo-001',
  status: 'OPEN',
  openedAt: new Date(Date.now() - SHIFT_OPENED_MS_AGO).toISOString(),
  cashierName: 'María López — Caja 1',
  // 1×$500 + 4×$100 + 4×$50 + 5×$20 + 10×$10 + 10×$5 + 10×$2 + 10×$1 + 10×$0.50 = $1,500.00
  openingFloatCents:
    1 * 50_000 + 4 * 10_000 + 4 * 5_000 + 5 * 2_000 + 10 * 1_000 + 10 * 500 + 10 * 200 + 10 * 100 + 10 * 50,
  openingDenoms: [
    { denomination: 50_000, count: 1 },
    { denomination: 10_000, count: 4 },
    { denomination: 5_000, count: 4 },
    { denomination: 2_000, count: 5 },
    { denomination: 1_000, count: 10 },
    { denomination: 500, count: 10 },
    { denomination: 200, count: 10 },
    { denomination: 100, count: 10 },
    { denomination: 50, count: 10 },
  ],
  moves: [
    { id: 'mv-demo-2', kind: 'IN', amountCents: 30_000, reason: 'Cambio adicional de la caja fuerte', at: new Date(Date.now() - 90 * 60 * 1000).toISOString() },
    { id: 'mv-demo-1', kind: 'OUT', amountCents: 12_500, reason: 'Compra de hielo y limones', at: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString() },
  ],
}
// ── End REGION: cash-drawer / shift seed ────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// REGION: /offline/cola ("Conexión") — additive, owned by the offline client.
// Sample queue of pending mutations for the no-backend preview. One entry is in
// `error` state. `ageMinutes` is converted to a real `queuedAt` at read time so
// the relative ages stay fresh. See lib/clients/offline.ts.
// ─────────────────────────────────────────────────────────────────────────────

export type SeedOfflineKind = 'comanda' | 'pago' | 'cierre' | 'otro'
export type SeedOfflineStatus = 'pendiente' | 'reintentando' | 'error'

export interface SeedOfflineEntry {
  id: string
  kind: SeedOfflineKind
  label: string
  ageMinutes: number
  status: SeedOfflineStatus
  attempts: number
  errorMessage?: string
}

export const SEED_OFFLINE_QUEUE: SeedOfflineEntry[] = [
  { id: 'oq-1', kind: 'comanda', label: 'Comanda C-8999 · enviar a cocina', ageMinutes: 2, status: 'pendiente', attempts: 0 },
  { id: 'oq-2', kind: 'pago', label: 'Pago $293.48 · mesa M3', ageMinutes: 5, status: 'reintentando', attempts: 2 },
  { id: 'oq-3', kind: 'cierre', label: 'Cierre de ticket A-1042', ageMinutes: 1, status: 'pendiente', attempts: 0 },
  { id: 'oq-4', kind: 'pago', label: 'Pago con tarjeta $512.00 · mesa M7', ageMinutes: 9, status: 'error', attempts: 4, errorMessage: 'Terminal de pago no responde' },
]
// ╭──────────────────────────────────────────────────────────────────────────╮
// │ P8 — Facturas (CFDI 4.0): seed data for the no-backend preview build.     │
// │ Additive region — appended for the billing module. Consumed by the        │
// │ FacturarSheet pre-fill + the billing client stubs. Cadena del proveedor   │
// │ fiscal: DocketHub → Omnifiscal → FiscalAPI → SAT (todo simulado).         │
// ╰──────────────────────────────────────────────────────────────────────────╯

/** A saved fiscal customer (CFDI receiver) for the preview. */
export interface PreviewFiscalCustomer {
  id: string
  rfc: string
  legalName: string
  tradeName: string | null
  email: string | null
  zipCode: string
  /** c_UsoCFDI default. */
  defaultCfdiUsage: string
  /** c_FormaPago default. */
  defaultPaymentForm: string
  /** c_RegimenFiscal. */
  fiscalRegimeCode: string
}

export const SEED_FISCAL_CUSTOMERS: PreviewFiscalCustomer[] = [
  {
    id: 'fc_publico', rfc: 'XAXX010101000', legalName: 'PÚBLICO EN GENERAL', tradeName: null,
    email: null, zipCode: '03100', defaultCfdiUsage: 'S01', defaultPaymentForm: '01', fiscalRegimeCode: '616',
  },
  {
    id: 'fc_duran', rfc: 'DUGG960512QX3', legalName: 'GUSTAVO DURAN GARCIA', tradeName: null,
    email: 'gustavo@example.mx', zipCode: '01219', defaultCfdiUsage: 'G03', defaultPaymentForm: '04', fiscalRegimeCode: '612',
  },
  {
    id: 'fc_sc', rfc: 'SCM150525XY1', legalName: 'SERVICIOS CREATIVOS MEXICO SA DE CV', tradeName: 'Servicios Creativos',
    email: 'admin@servicioscreativos.mx', zipCode: '03100', defaultCfdiUsage: 'G03', defaultPaymentForm: '03', fiscalRegimeCode: '601',
  },
]

/** An emitted CFDI 4.0 for the preview "Facturas" list. */
export interface PreviewCfdi {
  /** Folio fiscal (UUID). */
  uuid: string
  serie: string
  folio: string
  /** ISO timestamp emitido/timbrado. */
  issuedAt: string
  rfcReceptor: string
  razonSocialReceptor: string
  usoCfdi: string
  /** Total en centavos (incluye IVA 16%). */
  totalCents: number
  estado: 'vigente' | 'cancelada'
  /** Orden/ticket que originó el CFDI, si aplica. */
  orderId: string | null
  isGlobalDay: boolean
}

function isoP8(daysAgo: number): string {
  const d = new Date()
  d.setHours(13, 7, 0, 0)
  d.setDate(d.getDate() - daysAgo)
  return d.toISOString()
}

export const SEED_CFDIS: PreviewCfdi[] = [
  {
    uuid: 'A1B2C3D4-5E6F-4A7B-8C9D-0E1F2A3B4C5D', serie: 'A', folio: '1039', issuedAt: isoP8(0),
    rfcReceptor: 'DUGG960512QX3', razonSocialReceptor: 'GUSTAVO DURAN GARCIA', usoCfdi: 'G03 — Gastos en general',
    totalCents: 96280, estado: 'vigente', orderId: 'rcpt-demo-1', isGlobalDay: false,
  },
  {
    uuid: 'GGGGEEEE-7777-8888-9999-000011112222', serie: 'G', folio: '0007', issuedAt: isoP8(2),
    rfcReceptor: 'XAXX010101000', razonSocialReceptor: 'PÚBLICO EN GENERAL', usoCfdi: 'S01 — Sin efectos fiscales',
    totalCents: 1_875_000, estado: 'vigente', orderId: null, isGlobalDay: true,
  },
  {
    uuid: 'C4N3C3LL-AAAA-BBBB-CCCC-DDDDEEEEFFFF', serie: 'A', folio: '1031', issuedAt: isoP8(8),
    rfcReceptor: 'SCM150525XY1', razonSocialReceptor: 'SERVICIOS CREATIVOS MEXICO SA DE CV', usoCfdi: 'G03 — Gastos en general',
    totalCents: 580_000, estado: 'cancelada', orderId: null, isGlobalDay: false,
  },
]

/** Tickets PAGADO that don't have a CFDI yet — derived from SEED_RECEIPTS. */
export const SEED_UNINVOICED_TICKETS: Array<{ id: string; folio: string; totalCents: number; closedAt: string }> =
  SEED_RECEIPTS
    .filter((r) => !r.cfdi)
    .map((r) => ({ id: r.id, folio: r.order.folio, totalCents: r.order.totalCents, closedAt: r.closedAt }))
